export type TaskQueueOutcome = 'completed' | 'canceled';

type TaskQueueTask<T> = {
  item: T;
  resolve: (outcome: TaskQueueOutcome) => void;
  reject: (error: unknown) => void;
  abortController?: AbortController;
  canceled?: boolean;
};

const assertPositiveInteger = (value: number, name: string): void => {
  if (!Number.isInteger(value) || value < 1) {
    throw new Error(`${name} must be a positive integer.`);
  }
};

export class ConcurrentTaskQueue<T> {
  readonly #concurrency: number;
  readonly #tasks: Array<TaskQueueTask<T>> = [];
  readonly #worker: (item: T, signal: AbortSignal) => Promise<void>;
  readonly #inFlightTasks = new Set<TaskQueueTask<T>>();

  constructor(worker: (item: T, signal: AbortSignal) => Promise<void>, concurrency: number) {
    assertPositiveInteger(concurrency, 'concurrency');
    this.#concurrency = concurrency;
    this.#worker = worker;
  }

  enqueue(items: ReadonlyArray<T>): ReadonlyArray<Promise<TaskQueueOutcome>> {
    const tasks = items.map(
      (item) =>
        new Promise<TaskQueueOutcome>((resolve, reject) => {
          this.#tasks.push({ item, resolve, reject });
        }),
    );
    this.#drain();
    return tasks;
  }

  cancel(predicate: (item: T) => boolean): ReadonlyArray<T> {
    const canceled: T[] = [];
    for (let index = this.#tasks.length - 1; index >= 0; index -= 1) {
      const task = this.#tasks[index];
      if (predicate(task.item)) {
        this.#tasks.splice(index, 1);
        canceled.unshift(task.item);
        task.resolve('canceled');
      }
    }
    this.#inFlightTasks.forEach((task) => {
      if (!task.canceled && predicate(task.item)) {
        task.canceled = true;
        task.abortController?.abort();
        canceled.push(task.item);
      }
    });
    return canceled;
  }

  /** Cancels all queued work and aborts every active task. */
  invalidate(): ReadonlyArray<T> {
    return this.cancel(() => true);
  }

  /** Items whose work has started but not settled. Queued items are deliberately excluded. */
  getActiveItems(): ReadonlyArray<T> {
    return [...this.#inFlightTasks].map(({ item }) => item);
  }

  #drain(): void {
    while (this.#inFlightTasks.size < this.#concurrency) {
      const task = this.#tasks.shift();
      if (!task) {
        break;
      }

      const abortController = new AbortController();
      task.abortController = abortController;
      this.#inFlightTasks.add(task);
      void Promise.resolve()
        .then(() => this.#worker(task.item, abortController.signal))
        .then(
          () => this.#settleTask(task),
          (error: unknown) => this.#settleTask(task, { error }),
        );
    }
  }

  #settleTask(task: TaskQueueTask<T>, failure?: { error: unknown }): void {
    this.#inFlightTasks.delete(task);
    this.#drain();
    if (task.canceled) {
      task.resolve('canceled');
      return;
    }
    if (failure) {
      task.reject(failure.error);
      return;
    }
    task.resolve('completed');
  }
}
